import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

const BICICLO_LOGO_SRC = '/branding/biciclo-logo.png?v=20260605-2';

function getApiErrorMessage(error, fallback) {
  const payload = error?.response?.data;

  if (payload?.error) {
    return payload.error;
  }
  if (payload?.detail) {
    return payload.detail;
  }
  if (payload && typeof payload === 'object') {
    const firstEntry = Object.values(payload).find(Boolean);
    if (Array.isArray(firstEntry) && firstEntry.length > 0) {
      return firstEntry.join(', ');
    }
    if (typeof firstEntry === 'string') {
      return firstEntry;
    }
  }

  return fallback;
}

export default function ForgotPassword() {
  const { uid, token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isResetConfirmation = Boolean(uid && token);
  const initialAccountCode = useMemo(
    () => searchParams.get('account_code') || localStorage.getItem('account_code') || '',
    [searchParams],
  );

  const [accountCode, setAccountCode] = useState(initialAccountCode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedAccountCode = accountCode.trim().toLowerCase();
      await authAPI.requestPasswordReset({
        account_code: normalizedAccountCode,
        email: email.trim(),
      });
      localStorage.setItem('account_code', normalizedAccountCode);
      toast.success('Si le compte existe, un email de réinitialisation a été envoyé.');
      setEmail('');
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Impossible d'envoyer l'email de réinitialisation."));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();

    if (password !== password2) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);

    try {
      const normalizedAccountCode = accountCode.trim().toLowerCase();
      await authAPI.confirmPasswordReset({
        account_code: normalizedAccountCode,
        uid,
        token,
        password,
        password2,
      });
      localStorage.setItem('account_code', normalizedAccountCode);
      toast.success('Mot de passe réinitialisé. Vous pouvez vous connecter.');
      navigate('/login');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Le lien de réinitialisation est invalide ou expiré.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1.12fr)_minmax(20rem,32rem)] xl:grid-cols-[minmax(0,1.18fr)_minmax(22rem,34rem)] font-sans bg-[#0d1117]">
      <div className="relative hidden overflow-hidden p-8 lg:flex lg:flex-col lg:justify-between xl:p-12">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 h-[clamp(16rem,28vw,25rem)] w-[clamp(22rem,42vw,37.5rem)] -translate-x-1/4 translate-y-1/3 rounded-full bg-green-500 opacity-[0.07] blur-[80px]" />
          <div className="absolute top-0 right-0 h-[clamp(12rem,20vw,18.75rem)] w-[clamp(16rem,28vw,25rem)] -translate-y-1/3 translate-x-1/4 rounded-full bg-blue-500 opacity-[0.05] blur-[80px]" />
        </div>

        <div className="relative z-10 flex h-14 w-[clamp(10rem,24vw,12.5rem)] items-center justify-center rounded-2xl border border-emerald-400/15 bg-[#111827] px-4 shadow-[0_14px_28px_rgba(13,17,23,0.22)]">
          <img src={BICICLO_LOGO_SRC} alt="Logo Biciclo ERP" className="h-full w-auto max-w-none object-contain" />
        </div>

        <div className="relative z-10">
          <h1 className="mb-5 text-4xl font-bold leading-[1.1] tracking-tight text-[#f0f0ec] xl:text-5xl">
            Reprenez<br />la main sur <span className="text-green-400">votre accès.</span>
          </h1>
          <p className="max-w-xs text-[15px] leading-relaxed text-[rgba(240,240,236,0.5)]">
            Un lien sécurisé permet de définir un nouveau mot de passe pour le bon compte.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-[#f7f6f1] px-6 py-12 sm:px-8 sm:py-16 lg:px-12 xl:px-16">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex lg:hidden">
            <div className="flex h-12 w-[clamp(9.5rem,42vw,11.5rem)] items-center justify-center rounded-2xl border border-[#d7e8db] bg-[#111827] px-4 shadow-sm ring-1 ring-[#dfe7dd]">
              <img src={BICICLO_LOGO_SRC} alt="Logo Biciclo ERP" className="h-full w-auto max-w-none object-contain" />
            </div>
          </div>

          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-green-600">Sécurité</p>
          <h2 className="mb-2 text-3xl font-bold tracking-tight text-[#0d1117]">
            {isResetConfirmation ? 'Nouveau mot de passe' : 'Mot de passe oublié'}
          </h2>
          <p className="mb-10 text-sm text-[#888880]">
            {isResetConfirmation
              ? 'Choisissez un mot de passe robuste pour finaliser la réinitialisation.'
              : 'Indiquez votre compte et votre email pour recevoir un lien de réinitialisation.'}
          </p>

          <form onSubmit={isResetConfirmation ? handleConfirmReset : handleRequestReset} className="space-y-5">
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
              Code compte *
              <input
                type="text"
                value={accountCode}
                onChange={(event) => setAccountCode(event.target.value)}
                placeholder="ex. magasin_principal"
                required
                autoComplete="organization"
                className="w-full rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
              />
            </label>

            {!isResetConfirmation ? (
              <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                Email *
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoComplete="email"
                  className="w-full rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                />
              </label>
            ) : (
              <>
                <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                  Nouveau mot de passe *
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="w-full rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                  Confirmation *
                  <input
                    type="password"
                    value={password2}
                    onChange={(event) => setPassword2(event.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="w-full rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                  />
                </label>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#0d1117] py-3.5 text-sm font-semibold text-[#f7f6f1] transition-all hover:bg-[#1a2332] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Traitement...' : isResetConfirmation ? 'Modifier le mot de passe' : 'Envoyer le lien'}
            </button>
          </form>

          <p className="mt-6 text-center text-[13px] text-[#888880]">
            <Link to="/login" className="border-b border-green-500 pb-px font-semibold text-[#0d1117] transition-opacity hover:opacity-70">
              Retour à la connexion
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
